import React, { useMemo, type JSX } from 'react';
import type { EChartsOption } from 'echarts';

import EChartsBase from '../EChartsBase';
import useEChartsTheme from '../useEChartsTheme';

export type TransactionsHistoryChartDataItem = {
  months: number;
  count: number;
};

export interface TransactionsHistoryChartProps {
  data: TransactionsHistoryChartDataItem[];
  height?: number | string;
  renderer?: 'svg' | 'canvas';
  optionOverrides?: Record<string, unknown> | ((option: EChartsOption) => EChartsOption);
}

const TransactionsHistoryChart = React.memo(function TransactionsHistoryChart({
  data,
  height = 300,
  renderer = 'svg',
  optionOverrides,
}: TransactionsHistoryChartProps): JSX.Element {
  const theme = useEChartsTheme();
  const option = useMemo<EChartsOption>(
    () => ({
      animation: false,
      grid: { top: 16, right: 16, bottom: 32, left: 40, containLabel: true },
      tooltip: { trigger: 'axis' },
      xAxis: {
        type: 'category',
        data: data.map(({ months }) => String(months)),
        axisTick: { alignWithLabel: true },
        axisLabel: { color: theme.textSecondaryColor },
      },
      yAxis: {
        type: 'value',
        minInterval: 1,
        axisLabel: { color: theme.textSecondaryColor },
        splitLine: { lineStyle: { color: theme.gridColor } },
      },
      series: [
        {
          type: 'bar',
          data: data.map(({ count }) => count),
          itemStyle: { color: '#F2F2FF', borderColor: '#D9D9F5', borderWidth: 1 },
        },
      ],
    }),
    [data, theme],
  );

  let finalOption = option;
  if (typeof optionOverrides === 'function') {
    finalOption = optionOverrides(option);
  } else if (optionOverrides) {
    finalOption = { ...option, ...optionOverrides };
  }

  return (
    <EChartsBase option={finalOption} height={height} renderer={renderer} testId='echart-transactions-history-chart' />
  );
});

export default TransactionsHistoryChart;
